/**
 * Transaction DTO exposed by payment-service `/api/transactions`.
 * Mirrors `net.nawam.payment.model.entity.Transaction`.
 *
 * `metadata` is a free-form JSON string with a `type` discriminator —
 * see {@link TransactionMetadata}. Cross-service convention documented
 * in `CLAUDE.md` (Transaction Metadata Convention).
 */

import type { UserRef } from './user.types';

export type TransactionStatus =
  | 'INITIATED'
  | 'PENDING'
  | 'SUCCESSFUL'
  | 'FAILED'
  | 'EXPIRED';

/** Source service identifier (matches `Transaction.sourceService` values). */
export type SourceService =
  | 'rental'
  | 'wallet'
  | 'operator'
  | 'auth'
  | string;

export interface PaymentMethodRef {
  id?: number;
  name?: string | null;
  type?: string | null;
  /** Vendor code shown to GUTouch, e.g. `WAVE_SN_API_CASH_IN`. */
  serviceCode?: string | null;
  logoUrl?: string | null;
}

export interface Transaction {
  // ── BaseEntity ───────────────────────────────────────────────────────
  id: number;
  createdAt?: string | null;
  updatedAt?: string | null;

  // ── identity ──────────────────────────────────────────────────────────
  idFromClient: string;
  idFromGU?: string | null;
  numTransaction?: string | null;

  // ── money ─────────────────────────────────────────────────────────────
  amount: number;
  fees?: number | null;
  currency: string;

  // ── recipient ─────────────────────────────────────────────────────────
  recipientNumber?: string | null;
  recipientEmail?: string | null;
  recipientFirstName?: string | null;
  recipientLastName?: string | null;

  // ── lifecycle ─────────────────────────────────────────────────────────
  serviceCode: string;
  transactionStatus: TransactionStatus;
  paymentUrl?: string | null;
  qrCode?: string | null;
  deepLink?: string | null;
  maxit?: string | null;
  om?: string | null;
  callbackReceived: boolean;
  callbackReceivedAt?: string | null;

  // ── cross-service link ────────────────────────────────────────────────
  sourceService: SourceService;
  sourceEntityId: string;
  sourceEntityType?: string | null;

  // ── redirects ─────────────────────────────────────────────────────────
  returnUrl?: string | null;
  cancelUrl?: string | null;

  // ── parties ───────────────────────────────────────────────────────────
  userId?: number | null;
  /** Read-model du user qui a initié la transaction. */
  user?: UserRef | null;
  paymentMethod?: PaymentMethodRef | null;

  /** Free-form JSON string. Parse via {@link parseTransactionMetadata}. */
  metadata?: string | null;
}

// ── Metadata discriminator (CLAUDE.md convention) ──────────────────────

export type TransactionMetadataType =
  | 'RENTAL'
  | 'RIDE'
  | 'UV_PURCHASE'
  | 'WITHDRAWAL'
  | 'WALLET_TOPUP'
  | 'WALLET_WITHDRAWAL';

/** Union of all known metadata shapes (others fall back to `BaseMetadata`). */
export interface BaseMetadata {
  type: TransactionMetadataType | string;
  [key: string]: unknown;
}
export interface RentalMetadata extends BaseMetadata {
  type: 'RENTAL';
  vehicle?: string;
  immatriculation?: string;
  startDate?: string;
  endDate?: string;
  invoiceNumber?: string;
}
export interface RideMetadata extends BaseMetadata {
  type: 'RIDE';
  vehicle?: string;
  immatriculation?: string;
  startLocation?: string;
  endLocation?: string;
}
export interface UvPurchaseMetadata extends BaseMetadata {
  type: 'UV_PURCHASE';
  uvAmount?: number;
}
export interface WithdrawalMetadata extends BaseMetadata {
  type: 'WITHDRAWAL';
  source?: string;
}
export interface WalletTopupMetadata extends BaseMetadata {
  type: 'WALLET_TOPUP';
  ownerType?: 'OWNER' | 'DRIVER' | 'OPERATOR' | string;
  ownerRef?: number;
}
export interface WalletWithdrawalMetadata extends BaseMetadata {
  type: 'WALLET_WITHDRAWAL';
  ownerType?: 'OWNER' | 'DRIVER' | 'OPERATOR' | string;
  ownerRef?: number;
}

export type TransactionMetadata =
  | RentalMetadata
  | RideMetadata
  | UvPurchaseMetadata
  | WithdrawalMetadata
  | WalletTopupMetadata
  | WalletWithdrawalMetadata
  | BaseMetadata;

/** Safe JSON parse — returns `null` on failure or absent. */
export function parseTransactionMetadata(raw: string | null | undefined): TransactionMetadata | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed as TransactionMetadata;
    return null;
  } catch {
    return null;
  }
}

// ── UI helpers ─────────────────────────────────────────────────────────

import { defensiveStatusMeta } from '../helpers/status-meta';

/** Defensive : unknown/null statuses fall back to `{label:'—', palette:'gray'}`. */
export const TRANSACTION_STATUS_META = defensiveStatusMeta<TransactionStatus>({
  INITIATED: { label: 'Initiée', palette: 'gray' },
  PENDING: { label: 'En attente', palette: 'warning' },
  SUCCESSFUL: { label: 'Réussie', palette: 'brand' },
  FAILED: { label: 'Échouée', palette: 'error' },
  EXPIRED: { label: 'Expirée', palette: 'gray' },
});

export const METADATA_TYPE_LABEL: Record<string, string> = {
  RENTAL: 'Location',
  RIDE: 'Course',
  UV_PURCHASE: 'Achat UV',
  WITHDRAWAL: 'Retrait',
  WALLET_TOPUP: 'Recharge wallet',
  WALLET_WITHDRAWAL: 'Retrait wallet',
};

export const SOURCE_SERVICE_LABEL: Record<string, string> = {
  rental: 'Rental service',
  wallet: 'Wallet service',
  operator: 'Operator service',
  auth: 'Auth service',
};
